import React, { useState } from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import { Mail, Phone, Check } from "lucide-react";
import api from '../api/api';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await api.post('/contact', formData);
      
      if (response.data.success) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', message: '' });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        setSubmitError(response.data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitError(error.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      <main className="flex-1 flex flex-col">
        <section className="w-full bg-gray-100 pt-8 pb-12 px-4 sm:px-6 lg:px-16 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-7xl mx-auto">
            {/* Left Column: Text Content */}
            <div className="text-left relative overflow-visible">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="text-6xl md:text-8xl font-black text-black leading-none"
              >
                GET IN
                <br />
                T<span className="inline-block relative">
                  <span className="relative z-10">O</span>
                  <span className="absolute inset-0 bg-yellow-400 rounded-full transform scale-110 -z-0"></span>
                </span>UCH
              </motion.h1>
              
              <div className="relative overflow-hidden" style={{ height: '6px', marginTop: '32px', marginBottom: '32px' }}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.7 }}
                  className="bg-black absolute"
                  style={{ height: '6px', width: '100%', left: 0 }}
                ></motion.div>
              </div>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.7 }}
                className="mt-6 text-sm text-black max-w-md"
              >
                We'd love to hear from you! Whether you have a question about our services, pricing, or anything else, our team is ready to answer all your questions.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.7 }}
                className="mt-8 space-y-4"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-400 rounded-full">
                    <Mail size={20} className="text-white" />
                  </div>
                  <span className="text-black">admin@jobloop.in</span>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="bg-white p-8 shadow-sm w-full max-w-md mx-auto lg:mx-0">
              <h2 className="text-2xl font-bold mb-6">Send us a message</h2>
              
              {/* Success Message */}
              {submitSuccess && (
                <div className="mb-4 p-4 bg-green-100 border-2 border-green-500 text-green-700 flex items-center gap-2">
                  <Check size={20} />
                  <span className="font-medium">Message sent successfully! We'll get back to you soon.</span>
                </div>
              )}

              {/* Error Message */}
              {submitError && (
                <div className="mb-4 p-4 bg-red-100 border-2 border-red-500 text-red-700">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full p-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full p-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                    placeholder="Your email"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea 
                    id="message" 
                    rows={4} 
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full p-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                    placeholder="Your message"
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full font-bold bg-black text-white px-6 py-3 border-2 border-black hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      SENDING...
                    </>
                  ) : (
                    'SEND MESSAGE'
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* No map section since address is removed */}
      </main>
    </div>
  );
}
